import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, KeyRound, ArrowLeft, Loader2, Eye, EyeOff, Copy, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [issuedCode, setIssuedCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setIssuedCode(data.code || "");
      setCode(data.code || "");
      setStep(2);
      toast.success("Reset code generated");
    } catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const resetPwd = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      toast.success("Password updated! Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] glow-grid flex items-center justify-center px-5 py-12" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6"><ArrowLeft size={16} /> Back to sign in</Link>
        <div className="eco-card p-8">
          <div className="h-12 w-12 rounded-2xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center mb-5"><KeyRound size={22} className="text-[#4d8bff]" /></div>
          <h1 className="font-display text-2xl font-semibold text-white">Reset your password</h1>
          <p className="text-sm text-slate-400 mt-1.5">{step === 1 ? "Enter your account email to get a reset code." : "Enter the code and choose a new password."}</p>

          {step === 1 ? (
            <form onSubmit={requestCode} className="space-y-3 mt-6">
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email address" className="eco-input pl-10" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-request-btn" className="btn-accent w-full">{loading ? <Loader2 size={16} className="animate-spin" /> : "Send reset code"}</button>
            </form>
          ) : (
            <form onSubmit={resetPwd} className="space-y-3 mt-6">
              {issuedCode && (
                <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-3 flex items-center justify-between gap-2" data-testid="forgot-code-box">
                  <div>
                    <div className="text-[10px] font-mono-eco uppercase tracking-widest text-amber-300/80">Your code (email/SMS not configured)</div>
                    <div className="font-mono-eco text-lg text-white tracking-widest">{issuedCode}</div>
                  </div>
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(issuedCode); toast.success("Copied"); }} className="text-amber-300 hover:text-white"><Copy size={16} /></button>
                </div>
              )}
              <div className="relative">
                <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="forgot-code" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="6-digit code" className="eco-input pl-10" />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="forgot-newpass" type={show ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="New password" className="eco-input pl-10 pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-reset-btn" className="btn-accent w-full">{loading ? <Loader2 size={16} className="animate-spin" /> : <><CheckCircle2 size={16} /> Update password</>}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
